import * as React from 'react';
import { alpha, ThemeProvider, createTheme } from '@mui/material/styles';
import { useSuspenseQuery } from '@tanstack/react-query';
import { themeSettings } from '../theme';
import { PublicLayout } from '../components/layouts/PublicLayout';
import { getDiseaseStatsQuery, getReviewTypeStatsQuery } from '../api/statsApi';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import Divider from '@mui/material/Divider';
import CoronavirusIcon from '@mui/icons-material/Coronavirus';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import PublicIcon from '@mui/icons-material/Public';
import ThermostatIcon from '@mui/icons-material/Thermostat';
import HelpIcon from '@mui/icons-material/Help';
import heroBg from '../assets/images/home-hero-bg.jpg';
import { PublishedReviewsList } from './components/PublishedReviewsList';

const PUBLISHED_REVIEWS_GRID_KEY = 'home:published-reviews';


type SearchOption = { label: string; group: string };

const categoryMap: Record<string, { icon: React.ElementType; color: string }> = {
    disease:          { icon: CoronavirusIcon, color: '#ff802b' },
    study:            { icon: MenuBookIcon,    color: '#1c7592' },
    country:          { icon: PublicIcon,      color: '#017507' },
    climate_variable: { icon: ThermostatIcon,  color: '#e43838' },
};

const heroDarkTheme = createTheme(themeSettings('dark'));

export const Home = () => {
    const { data } = useSuspenseQuery(getDiseaseStatsQuery());
    const { data: reviewTypeStats } = useSuspenseQuery(getReviewTypeStatsQuery());

    const searchOptions: SearchOption[] = [
        ...data.map(d => ({ label: d.name, group: 'Disease' })),
        { label: 'Kenya', group: 'Country' },
        { label: 'Democratic Republic of Congo', group: 'Country' },
        { label: 'South Africa', group: 'Country' },
        { label: 'Sudan', group: 'Country' },
        { label: 'Uganda', group: 'Country' },
        { label: 'Rainfall', group: 'Climate Variable' },
        { label: 'Temperature', group: 'Climate Variable' },
        { label: 'Humidity', group: 'Climate Variable' },
        { label: 'NDVI', group: 'Climate Variable' },
        { label: 'Flooding', group: 'Climate Variable' },
    ];


    const [searchValue, setSearchValue] = React.useState<SearchOption | null>(null);

    return (
        <PublicLayout>
            {/* Hero */}
            <Box sx={{
                position: 'relative',
                bgcolor: 'background.paper',
                py: 10,
                textAlign: 'center',
                borderBottom: 1,
                borderColor: 'divider',
                backgroundImage: `url(${heroBg})`,
                backgroundSize: 'cover',
                backgroundPosition: 'bottom left',
                backgroundRepeat: 'no-repeat',
            }}>
                <Box
                    component="a"
                    href="https://unsplash.com/@betagamma?utm_source=unsplash&utm_medium=referral&utm_content=creditCopyText"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                        position: 'absolute',
                        bottom: 8,
                        right: 8,
                        px: 1,
                        py: 0.25,
                        borderRadius: 1,
                        bgcolor: 'rgba(0, 0, 0, 0.16)',
                        color: 'rgba(255, 255, 255, 0.23)',
                        fontSize: '0.45rem',
                        textDecoration: 'none',
                        '&:hover': { color: '#ffffff70' },
                    }}
                >
                    Photo by Daniil Silantev on Unsplash
                </Box>
                <ThemeProvider theme={heroDarkTheme}>
                    <Container maxWidth="md">
                        <Typography variant="h2" fontWeight={700} gutterBottom color="text.primary">
                            Climate-Sensitive Zoonotic Disease Database
                        </Typography>
                        <Typography variant="body1" color="text.secondary" sx={{ mb: 5 }}>
                            A collaborative repository of disease modelling parameters extracted from literature.
                        </Typography>
                        <Autocomplete
                            fullWidth
                            options={searchOptions}
                            groupBy={(option) => option.group}
                            getOptionLabel={(option) => option.label}
                            value={searchValue}
                            onChange={(_, newValue) => setSearchValue(newValue)}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    placeholder="Search by disease, country, or climate variable..."
                                    variant="outlined"
                                />
                            )}
                        />
                    </Container>
                </ThemeProvider>
            </Box>

            {/* Stats strip */}
            <Box sx={{ display: { xs: 'none', sm: 'block' }, bgcolor: 'action.hover', py: 4, borderBottom: 1, borderColor: 'divider' }}>
                <Container maxWidth="lg">
                    <Box sx={{ display: 'flex', justifyContent: 'space-around', flexWrap: 'wrap', gap: 3 }}>
                        {reviewTypeStats.map((stat, i) => {
                            const { icon: Icon, color } = categoryMap[stat.category] ?? { icon: HelpIcon, color: '#666' };
                            return (
                                <React.Fragment key={stat.label}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, px: 3 }}>
                                        <Box sx={{
                                            display: 'flex',
                                            width: 70,
                                            height: 70,
                                            borderRadius: 50,
                                            bgcolor: alpha(color, 0.1),
                                            justifyContent: 'center',
                                            alignItems: 'center'
                                        }}>
                                            <Icon sx={{ 
                                                fontSize: 40, 
                                                color: color,
                                            }} />
                                        </Box>
                                        <Box>
                                            <Typography variant="h3" fontWeight={700} color="info">{stat.value}</Typography>
                                            <Typography variant="body2" color="text.secondary">{stat.label}</Typography>
                                        </Box>
                                    </Box>
                                    {i < reviewTypeStats.length - 1 && (
                                        <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' } }} />
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </Box>
                </Container>
            </Box>

            {/* Recent reviews grid */}
            <Container maxWidth="lg" sx={{ py: 6 }}>
                <PublishedReviewsList storageKey={PUBLISHED_REVIEWS_GRID_KEY} />
            </Container>
        </PublicLayout>
    );
};
